// src/components/PermissionManager.jsx
import React, { useState } from 'react';

const availablePermissions = [
  // Utilisateurs
  { id: 'users.view', label: 'Voir les utilisateurs', category: 'Utilisateurs' },
  { id: 'users.create', label: 'Créer des utilisateurs', category: 'Utilisateurs' },
  { id: 'users.edit', label: 'Modifier les utilisateurs', category: 'Utilisateurs' },
  { id: 'users.delete', label: 'Supprimer des utilisateurs', category: 'Utilisateurs' },
  
  // Rôles
  { id: 'roles.view', label: 'Voir les rôles', category: 'Rôles' },
  { id: 'roles.create', label: 'Créer des rôles', category: 'Rôles' },
  { id: 'roles.edit', label: 'Modifier les rôles', category: 'Rôles' },
  { id: 'roles.delete', label: 'Supprimer des rôles', category: 'Rôles' },
  
  // Entreprises
  { id: 'companies.view', label: 'Voir les entreprises', category: 'Entreprises' },
  { id: 'companies.edit', label: 'Modifier les entreprises', category: 'Entreprises' },
  
  // Leads
  { id: 'leads.view', label: 'Voir les leads', category: 'Leads' },
  { id: 'leads.create', label: 'Créer des leads', category: 'Leads' },
  { id: 'leads.edit', label: 'Modifier les leads', category: 'Leads' },
  { id: 'leads.delete', label: 'Supprimer des leads', category: 'Leads' },
  { id: 'leads.assign', label: 'Assigner des leads', category: 'Leads' },
  
  // Campagnes
  { id: 'campaigns.view', label: 'Voir les campagnes', category: 'Campagnes' },
  { id: 'campaigns.create', label: 'Créer des campagnes', category: 'Campagnes' },
  { id: 'campaigns.edit', label: 'Modifier les campagnes', category: 'Campagnes' },
  
  // Rapports
  { id: 'reports.view', label: 'Voir les rapports', category: 'Rapports' },
  { id: 'reports.export', label: 'Exporter les rapports', category: 'Rapports' },
  
  // Paramètres
  { id: 'settings.view', label: 'Voir les paramètres', category: 'Paramètres' },
  { id: 'settings.edit', label: 'Modifier les paramètres', category: 'Paramètres' },
];

const PermissionManager = ({ role, onClose, onSave }) => {
  const [selectedPermissions, setSelectedPermissions] = useState(
    role.permissions || []
  );

  // Grouper par catégorie
  const groupedPermissions = availablePermissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {});

  const togglePermission = (permId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permId)) {
        return prev.filter(p => p !== permId);
      } else {
        return [...prev, permId];
      }
    });
  };

  const toggleCategory = (category, perms) => {
    const permIds = perms.map(p => p.id);
    const allSelected = permIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(p => !permIds.includes(p)));
    } else {
      setSelectedPermissions(prev => [
        ...prev,
        ...permIds.filter(id => !prev.includes(id))
      ]);
    }
  };

  const selectAll = () => {
    setSelectedPermissions(availablePermissions.map(p => p.id));
  };

  const selectNone = () => {
    setSelectedPermissions([]);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-8 border w-[900px] shadow-xl rounded-xl bg-white">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Permissions pour {role.name}
            </h3>
            <p className="text-gray-600 mt-1">
              {selectedPermissions.length} permission(s) sélectionnée(s)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="flex space-x-3 mb-6">
          <button
            onClick={selectAll}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Tout sélectionner
          </button>
          <button
            onClick={selectNone}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Tout désélectionner
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto pr-2 space-y-6">
          {Object.entries(groupedPermissions).map(([category, perms]) => (
            <div key={category} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-3 pb-2 border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={perms.every(p => selectedPermissions.includes(p.id))}
                  onChange={() => toggleCategory(category, perms)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <h4 className="ml-2 text-lg font-semibold text-gray-800">
                  {category}
                </h4>
                <span className="ml-2 text-xs text-gray-500">
                  ({perms.length})
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 ml-6">
                {perms.map(perm => (
                  <div key={perm.id} className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(perm.id)}
                      onChange={() => togglePermission(perm.id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-0.5"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      {perm.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors bg-white font-medium"
          >
            Annuler
          </button>
          <button
            onClick={() => onSave(selectedPermissions)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionManager;